import clsx from '@vangst/lib/clsx'
import useCompany, {
  CompanyOptions,
  CompanyVars,
} from '@vangst/services/oogst/company/useCompany'
import useFollow from '@vangst/services/oogst/company/useFollow'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { memo, useState } from 'react'
import { MdErrorOutline } from 'react-icons/md'
import { DialogModal } from '../../components/feedback/DialogModal'
import ClickyBtn from '../../components/navigation/ClickyBtn'
import ClickyLink from '../../components/navigation/ClickyLink'

type PropsType = React.HTMLAttributes<HTMLElement> & {
  readonly as: 'btn' | 'link'
  readonly restricted?: boolean
  readonly variables: CompanyVars
  readonly options?: CompanyOptions
  readonly btnTxt?: string
}

function FollowEditSignup(props: PropsType) {
  const { as, btnTxt, restricted = false, variables, options, ...rest } = props
  const [showAlert, setShowAlert] = useState(false)
  const { authenticated, authenticating } = useViewer()
  const unauthenticated = !authenticated && !authenticating

  const { data } = useCompany(variables, options)
  const company = data?.getClient

  const { followCompany, unfollowCompany } = useFollow(company)
  const show = () => setShowAlert(true)
  const hide = () => setShowAlert(false)
  const handleClickUnfollow = () => {
    unfollowCompany?.()
    hide()
  }
  const btnStyle = restricted
    ? 'mood-blue-dark ml-2 min-w-60'
    : 'mood-blue-dark w-full'
  const btnStyleFollowing = restricted
    ? 'mood-orange min-w-60'
    : 'mood-orange w-full'

  if (unauthenticated) {
    return as === 'link' ? (
      <ClickyLink
        as="text-icon"
        className={clsx('link-blue-dark z-20 text-sm', props.className)}
        href={Routes.SIGN_UP}
        {...rest}
      >
        Sign Up
      </ClickyLink>
    ) : (
      <ClickyBtn
        className={clsx(btnStyle, props.className)}
        href={Routes.SIGN_UP}
        {...rest}
      >
        Sign Up
      </ClickyBtn>
    )
  }

  if (company?.permissions.edit && company.routes) {
    return as === 'link' ? (
      <ClickyLink
        as="text-icon"
        className={clsx('z-20 text-sm text-green', props.className)}
        href={company.routes.edit}
        {...rest}
      >
        Edit Info
      </ClickyLink>
    ) : (
      <ClickyBtn
        className={clsx(btnStyle, props.className, 'mood-green')}
        href={company.routes.edit}
        {...rest}
      >
        Edit Information
      </ClickyBtn>
    )
  }

  if (followCompany) {
    return as === 'link' ? (
      <ClickyLink
        as="text-icon"
        className={clsx('link-blue-dark z-20 text-sm', props.className)}
        onClick={followCompany}
        {...rest}
      >
        Follow
      </ClickyLink>
    ) : (
      <ClickyBtn
        className={clsx(btnStyle, props.className)}
        onClick={followCompany}
        {...rest}
      >
        Follow
      </ClickyBtn>
    )
  }

  if (unfollowCompany) {
    return (
      <>
        {' '}
        {as === 'link' ? (
          <ClickyLink
            as="text-icon"
            className={clsx('link-grey z-20 text-sm', props.className)}
            onClick={show}
            {...rest}
          >
            Following
          </ClickyLink>
        ) : (
          <ClickyBtn
            className={clsx(btnStyleFollowing, props.className)}
            onClick={show}
            {...rest}
          >
            Following
          </ClickyBtn>
        )}
        {showAlert && (
          <DialogModal onClose={hide} open>
            <DialogModal.Panel icon={MdErrorOutline} title="Unfollow?">
              <DialogModal.Description className="my-8">
                Click okay to remove this connection from your network. We won't
                notify the other party of the disconnection. This profile will
                no longer display as a connection on your profile.
              </DialogModal.Description>
              <div className="flex w-full items-center justify-center gap-4">
                <ClickyBtn className="mood-white w-full" onClick={hide}>
                  Cancel
                </ClickyBtn>
                <ClickyBtn
                  className="mood-orange w-full"
                  onClick={handleClickUnfollow}
                >
                  OK
                </ClickyBtn>
              </div>
            </DialogModal.Panel>
          </DialogModal>
        )}
      </>
    )
  }

  // loading...
  return null
}

export default memo(FollowEditSignup)
